import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, PackageOpen, Download, Trash2 } from 'lucide-react';
import { periodDetailOptions } from '../../lib/resources/queries';
import {
  useDeleteExpenseItem, useFinalizeSubmission, useUploadSubInvoice,
} from '../../lib/resources/mutations';
import { parseError } from '../../lib/api/client';
import type { PeriodSummary } from '../../lib/resources/types';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { StatusPip, statusPipForSubmission } from '../../components/ui/status-pip';
import { EmptyState } from '../../components/ui/empty-state';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { KeyValueRow } from '../../components/ui/key-value-row';
import { formatMoney, monthLabel } from '../../lib/utils';
import { SummaryCell } from './summary-cell';
import { ExpenseAddForm } from './expense-add-form';

interface CurrentMonthCardProps {
  period: PeriodSummary;
  currency: string;
}

export function CurrentMonthCard({ period, currency }: CurrentMonthCardProps) {
  const detail = useQuery(periodDetailOptions(period.id));
  const deleteItem = useDeleteExpenseItem(period.id);
  const upload = useUploadSubInvoice(period.id);
  const finalize = useFinalizeSubmission(period.id);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [finalizeError, setFinalizeError] = useState<string | null>(null);

  if (detail.isLoading || !detail.data) {
    return (
      <Card>
        <CardHeader><CardTitle>{monthLabel(period.year, period.month)}</CardTitle></CardHeader>
        <CardContent className="flex items-center gap-2 text-foreground-muted text-[13px]">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </CardContent>
      </Card>
    );
  }

  const d = detail.data;
  const isFinalized = d.submission.status === 'FINALIZED';
  const isVoided = d.submission.status === 'VOIDED';
  // LOCKED period = admin finalized the master invoice. Backend rejects any
  // mutation; reflect that in the UI by hiding the editor + Finalize button.
  const isLocked = period.status === 'LOCKED';
  const editable = !isFinalized && !isVoided && !isLocked;

  async function onFile(file: File | null) {
    if (!file) return;
    setUploadError(null);
    try {
      await upload.mutateAsync({ submissionId: d.submission.id, file });
    } catch (e) {
      const err = await parseError(e);
      setUploadError(err.message || 'Upload failed');
    }
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle>{monthLabel(period.year, period.month)}</CardTitle>
            <StatusPip {...statusPipForSubmission(d.submission.status)} />
            {d.submission.generatedInvoiceNumber && (
              <span className="font-mono text-[12px] text-foreground-muted">
                {d.submission.generatedInvoiceNumber}
              </span>
            )}
          </div>
          {isFinalized ? (
            <Button asChild variant="secondary" size="sm">
              <a
                href={`/api/files/contractor-invoice/${d.submission.id}/download`}
                target="_blank"
                rel="noreferrer"
              >
                <Download className="size-3.5" /> Download PDF
              </a>
            </Button>
          ) : editable ? (
            <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={finalize.isPending}>
              {finalize.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Finalize {monthLabel(period.year, period.month)}
            </Button>
          ) : isLocked ? (
            <StatusPip severity="info" label="Period locked" />
          ) : null}
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-6 mb-6">
            <SummaryCell label="Base salary" value={formatMoney(d.baseSalaryCents, currency)} mono />
            <SummaryCell
              label="Expenses"
              value={formatMoney(d.runningTotalCents - d.baseSalaryCents, currency)}
              mono
            />
            <SummaryCell label="Running total" value={formatMoney(d.runningTotalCents, currency)} mono highlight />
          </div>

          {d.items.length === 0 && !isFinalized ? (
            <EmptyState
              icon={PackageOpen}
              title={isLocked
                ? `${monthLabel(period.year, period.month)} is locked`
                : `Add your first expense item for ${monthLabel(period.year, period.month)}`}
              description={isLocked
                ? 'Admin has already finalized the master invoice for this period — it can no longer be edited.'
                : 'Add a label + amount. Receipts attach via the upload button below.'}
            />
          ) : (
            <div className="border border-border rounded-[--radius-input] overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-background">
                  <tr className="text-[11px] uppercase tracking-wide text-foreground-muted">
                    <th className="text-left px-3 py-2 font-medium">Label</th>
                    <th className="text-right px-3 py-2 font-medium w-32">Amount</th>
                    {editable && <th className="w-12 px-3 py-2" />}
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((item) => (
                    <tr key={item.id} className="border-t border-border">
                      <td className="px-3 py-2">{item.label}</td>
                      <td className="px-3 py-2 text-right font-mono">{formatMoney(item.amountCents, currency)}</td>
                      {editable && (
                        <td className="px-3 py-2">
                          <Button size="sm" variant="ghost" onClick={() => deleteItem.mutate(item.id)}>
                            <Trash2 className="size-3.5" />
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {editable && (
            <ExpenseAddForm
              periodId={period.id}
              submissionId={d.submission.id}
              onFile={onFile}
              uploading={upload.isPending}
              uploadError={uploadError}
            />
          )}

          {d.subInvoices.length > 0 && (
            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wide text-foreground-muted mb-2">
                Receipts ({d.subInvoices.length})
              </div>
              <ul className="divide-y divide-border border border-border rounded-[--radius-input]">
                {d.subInvoices.map((s) => (
                  <li key={s.id} className="flex items-center gap-3 px-3 py-2 text-[13px]">
                    <span className="flex-1 truncate">{s.originalFilename}</span>
                    {s.sizeBytes != null && (
                      <span className="text-[11px] text-foreground-muted font-mono">
                        {(s.sizeBytes / 1024).toFixed(1)} KB
                      </span>
                    )}
                    <a
                      href={`/api/files/sub-invoice/${s.id}/download`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[12px] text-primary hover:underline"
                    >
                      Open
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Finalize {monthLabel(period.year, period.month)}?</DialogTitle>
            <DialogDescription>
              This generates your invoice PDF and assigns the next invoice number from your sequence.
              You can void + recreate later if needed, but the assigned number stays consumed.
            </DialogDescription>
          </DialogHeader>
          <div className="text-[13px] space-y-1">
            <KeyValueRow label="Base" value={formatMoney(d.baseSalaryCents, currency)} />
            <KeyValueRow label="Expenses" value={formatMoney(d.runningTotalCents - d.baseSalaryCents, currency)} />
            <div className="pt-2 mt-2 border-t-2 border-foreground">
              <KeyValueRow label="Total" value={formatMoney(d.runningTotalCents, currency)} bold />
            </div>
          </div>
          {finalizeError && (
            <div className="text-[12px] text-error" role="alert">{finalizeError}</div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              onClick={async () => {
                setFinalizeError(null);
                try {
                  await finalize.mutateAsync();
                  setConfirmOpen(false);
                } catch (e) {
                  const err = await parseError(e);
                  setFinalizeError(err.message || 'Finalize failed');
                }
              }}
              disabled={finalize.isPending}
            >
              {finalize.isPending && <Loader2 className="size-3.5 animate-spin" />}
              Finalize
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
